/**
 * Meeting Search and Filter JavaScript
 *
 * Provides dynamic search and filtering for meetings and committees.
 *
 * @package
 * @since 2.0.0
 * @param {Object} $ - jQuery object
 */

/* global flatpickr, DemocraticSearch */

(function ($) {
	'use strict';

	const MeetingSearch = {
		$searchForm: null,
		$resultsContainer: null,
		searchTimeout: null,

		/**
		 * Initialize search functionality
		 */
		init() {
			this.$searchForm = $('#democratic-search-form');
			this.$resultsContainer = $('#search-results');

			// Only initialize if this is the meeting search form (has #search-query).
			// Don't initialize on global search widget pages (which use #democratic-search-input).
			if (!this.$searchForm.length || !$('#search-query').length) return;

			this.bindEvents();
			this.initDatePicker();
		},

		/**
		 * Bind event handlers
		 */
		bindEvents() {
			// Search input with debounce
			$('#search-query').on('input', () => {
				clearTimeout(this.searchTimeout);
				this.searchTimeout = setTimeout(() => {
					this.performSearch();
				}, 500);
			});

			// Filter changes
			$('.search-filter').on('change', () => this.performSearch());

			// Clear filters
			$('#clear-filters-btn').on('click', () => this.clearFilters());

			// Form submit
			this.$searchForm.on('submit', (e) => {
				e.preventDefault();
				this.performSearch();
			});
		},

		/**
		 * Initialize date picker
		 */
		initDatePicker() {
			if (typeof flatpickr !== 'undefined') {
				flatpickr('#date-from', {
					dateFormat: 'Y-m-d',
					onChange: () => this.performSearch(),
				});

				flatpickr('#date-to', {
					dateFormat: 'Y-m-d',
					onChange: () => this.performSearch(),
				});
			}
		},

		/**
		 * Perform search
		 */
		async performSearch() {
			const searchData = this.getSearchParams();

			this.showLoading();

			try {
				const response = await $.ajax({
					url: DemocraticSearch.ajaxUrl,
					method: 'GET',
					data: {
						action: 'democratic_search_meetings',
						...searchData,
					},
				});

				if (response.success) {
					this.renderResults(response.data);
				} else {
					this.showError(response.data.message || 'Search failed');
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Search error:', error);
				this.showError('An error occurred while searching');
			}
		},

		/**
		 * Get search parameters from form
		 *
		 * @return {Object} Search parameters object
		 */
		getSearchParams() {
			const params = {
				query: $('#search-query').val(),
				committee: $('#filter-committee').val(),
				status: $('#filter-status').val(),
				date_from: $('#date-from').val(),
				date_to: $('#date-to').val(),
				type: $('#filter-type').val(),
				page: 1,
			};
			if (typeof DemocraticSearch !== 'undefined' && DemocraticSearch.nonce) {
				params.nonce = DemocraticSearch.nonce;
			}
			return params;
		},

		/**
		 * Clear all filters
		 */
		clearFilters() {
			$('#search-query').val('');
			$('.search-filter').val('');
			this.performSearch();
		},

		/**
		 * Show loading state
		 */
		showLoading() {
			this.$resultsContainer.html(`
                <div class="search-loading">
                    <div class="spinner"></div>
                    <p>Searching...</p>
                </div>
            `);
		},

		/**
		 * Show error message
		 *
		 * @param {string} message - Error message to display
		 */
		showError(message) {
			this.$resultsContainer.html(`
                <div class="search-error">
                    <p class="error-message">${this.escapeHtml(message)}</p>
                </div>
            `);
		},

		/**
		 * Render search results
		 *
		 * @param {Object} data - Search results data
		 */
		renderResults(data) {
			if (!data.results || data.results.length === 0) {
				this.$resultsContainer.html(`
                    <div class="no-results">
                        <p>No meetings found matching your criteria.</p>
                    </div>
                `);
				return;
			}

			let html = '<div class="search-results-list">';
			html += `<div class="results-header">
                <span class="results-count">${data.total} results found</span>
            </div>`;

			data.results.forEach((meeting) => {
				html += this.renderMeetingCard(meeting);
			});

			html += '</div>';

			if (data.pagination) {
				html += this.renderPagination(data.pagination);
			}

			this.$resultsContainer.html(html);
		},

		/**
		 * Render meeting card
		 *
		 * @param {Object} meeting - Meeting object
		 * @return {string} HTML string for meeting card
		 */
		renderMeetingCard(meeting) {
			const dateObj = new Date(meeting.date);

			return `
                <div class="meeting-card">
                    <div class="meeting-date">
                        <span class="day">${dateObj.getDate()}</span>
                        <span class="month">${dateObj.toLocaleDateString('en-GB', { month: 'short' })}</span>
                    </div>
                    <div class="meeting-content">
                        <h3 class="meeting-title">
                            <a href="${meeting.url}">${this.escapeHtml(meeting.title)}</a>
                        </h3>
                        <div class="meeting-meta">
                            <span class="meeting-committee">${this.escapeHtml(meeting.committee)}</span>
                            <span class="meeting-time">${meeting.time}</span>
                            <span class="meeting-venue">${this.escapeHtml(meeting.venue)}</span>
                        </div>
                        ${meeting.status ? `<span class="meeting-status status-${meeting.status}">${meeting.status_label}</span>` : ''}
                    </div>
                    <div class="meeting-actions">
                        ${meeting.agenda_url ? `<a href="${meeting.agenda_url}" class="btn-agenda">View Agenda</a>` : ''}
                        ${meeting.minutes_url ? `<a href="${meeting.minutes_url}" class="btn-minutes">View Minutes</a>` : ''}
                    </div>
                </div>
            `;
		},

		/**
		 * Render pagination
		 *
		 * @param {Object} pagination - Pagination data
		 * @return {string} HTML string for pagination
		 */
		renderPagination(pagination) {
			let html = '<div class="search-pagination">';

			if (pagination.prev) {
				html += `<button class="page-link" data-page="${pagination.prev}">Previous</button>`;
			}

			for (let i = 1; i <= pagination.total_pages; i++) {
				const activeClass =
					i === pagination.current_page ? 'active' : '';
				html += `<button class="page-link ${activeClass}" data-page="${i}">${i}</button>`;
			}

			if (pagination.next) {
				html += `<button class="page-link" data-page="${pagination.next}">Next</button>`;
			}

			html += '</div>';

			// Bind pagination events
			setTimeout(() => {
				$('.page-link').on('click', (e) => {
					const page = $(e.currentTarget).data('page');
					this.performSearch(page);
				});
			}, 0);

			return html;
		},

		/**
		 * Escape HTML
		 *
		 * @param {string} text - Text to escape
		 * @return {string} Escaped HTML string
		 */
		escapeHtml(text) {
			const map = {
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				'"': '&quot;',
				"'": '&#039;',
			};
			return String(text).replace(/[&<>"']/g, (m) => map[m]);
		},
	};

	// Initialize on document ready
	$(document).ready(() => {
		MeetingSearch.init();
	});
})(jQuery);
